import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Loader } from '@wsc_sports/components-library'
import VideoElement from './VideoElement'
import { ControlsWrapper } from './controls/ControlsWrapper'
import { StyledCenterPosition, StyledLoader } from '../StyledApp.styled'

export interface Props {
  mediaSrc: string
  mediaTitle: string
  setIsFadeIn: (isFadeIn: boolean) => void
  isFadeIn: boolean
}

export const VideoPlayer = ({
  mediaSrc,
  mediaTitle,
  setIsFadeIn,
  isFadeIn,
}: Props): JSX.Element => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const fadeInTimeoutRef = useRef()
  const [isPlaying, setIsPlaying] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
        setIsPlaying(false)
      } else {
        videoRef.current.play()
        setIsPlaying(true)
      }
    }
  }

  const handleMediaClick = () => {
    handlePlayPause()
  }

  const handleLoadedMetadata = (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    setDuration(event.currentTarget.duration)
  }

  const handleTimeUpdate = (event: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    setCurrentTime(event.currentTarget.currentTime)
  }

  useEffect(() => {
    const resetTimeout = () => {
      if (fadeInTimeoutRef.current) {
        clearTimeout(fadeInTimeoutRef.current)
      }

      if (isPlaying) {
        setIsFadeIn(true)
        fadeInTimeoutRef.current = setTimeout(() => setIsFadeIn(false), 1500)
      }
    }

    document.addEventListener('mousemove', resetTimeout)
    document.addEventListener('click', resetTimeout)

    return () => {
      if (fadeInTimeoutRef.current) {
        clearTimeout(fadeInTimeoutRef.current)
      }
      document.removeEventListener('mousemove', resetTimeout)
      document.removeEventListener('click', resetTimeout)
    }
  }, [isPlaying, setIsFadeIn])

  useEffect(() => {
    if (videoRef.current && !isPlaying) {
      setIsFadeIn(true)
    }
  }, [isPlaying, setIsFadeIn])

  const [isLoading, setIsLoading] = useState(false)

  const handleWaiting = useCallback(() => setIsLoading(true), [])
  const handlePlaying = useCallback(() => setIsLoading(false), [])

  useEffect(() => {
    // By copying videoRef.current to a local variable,
    // we ensure that the same reference is used in both the event listener setup and cleanup functions,
    // preventing potential issues with a changing ref.
    const videoElement = videoRef.current

    if (videoElement) {
      videoElement.addEventListener('waiting', handleWaiting)
      videoElement.addEventListener('playing', handlePlaying)
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener('waiting', handleWaiting)
        videoElement.removeEventListener('playing', handlePlaying)
      }
    }
  }, [handleWaiting, handlePlaying])

  return (
    <>
      {isLoading && (
        <StyledLoader>
          <StyledCenterPosition>
            <Loader size="2.857rem" />
          </StyledCenterPosition>
        </StyledLoader>
      )}

      <VideoElement
        ref={videoRef}
        mediaSrc={mediaSrc}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
        handleMediaClick={handleMediaClick}
      />

      <ControlsWrapper
        videoRef={videoRef}
        videoTitle={mediaTitle}
        isPlaying={isPlaying}
        onPlayPause={handlePlayPause}
        setIsPlaying={setIsPlaying}
        currentTime={currentTime}
        duration={duration}
        setCurrentTime={setCurrentTime}
        isFadeIn={isFadeIn}
      />
    </>
  )
}

export default VideoPlayer
